import React from 'react'
import ServicePage, { SplitSection, StackRibbon } from '../ServicePage'

const ResourcingHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="r-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="r-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#r-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* connection lines */}
    <g stroke="rgba(0,212,255,0.35)" strokeDasharray="4 6" fill="none">
      <path d="M230 230 L120 130" />
      <path d="M230 230 L340 130" />
      <path d="M230 230 L120 330" />
      <path d="M230 230 L340 330" />
      <path d="M230 230 L230 360" />
      <path d="M230 230 L80 240" />
      <path d="M230 230 L380 240" />
    </g>

    {/* central node */}
    <g>
      <circle cx="230" cy="230" r="44" fill="rgba(0,212,255,0.10)" stroke="rgba(0,212,255,0.6)" />
      <circle cx="230" cy="230" r="22" fill="url(#r-grad)" />
      <text x="230" y="235" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fill="#fff" fontSize="14">QA</text>
    </g>

    {/* people nodes */}
    {[
      [120, 130, 'SDET'],
      [340, 130, 'DEV'],
      [120, 330, 'PM'],
      [340, 330, 'BA'],
      [230, 360, 'OPS'],
      [80, 240, 'UX'],
      [380, 240, 'SEC'],
    ].map(([x, y, label], i) => (
      <g key={i}>
        <circle cx={x} cy={y} r="26" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.4)" />
        <text x={x} y={y + 4} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="#00d4ff">{label}</text>
        <animate attributeName="opacity" values="0.7;1;0.7" dur={`${3 + i * 0.3}s`} repeatCount="indefinite" />
      </g>
    ))}
  </svg>
)

const TechnicalResourcing = () => (
  <ServicePage
    slug="technical-resourcing"
    seo={{
      title: 'Technical Resourcing & QA Recruitment',
      description:
        'Hire pre-vetted QA engineers, SDETs, developers and DevOps specialists from QA Tech. Flexible contracts, full-time placements and dedicated squads — UK and remote.',
      keywords:
        'technical resourcing, QA recruitment, SDET hire, software engineer hire, dedicated QA team, QA outsourcing, contract testers, IT staff augmentation, UK tech recruitment',
    }}
    hero={{
      eyebrow: 'Service / Technical Resourcing',
      title: 'Engineers who hit the ground running.',
      lede:
        'Pre-vetted QA engineers, SDETs, developers and DevOps specialists — embedded into your team in weeks, not quarters.',
    }}
    visual={<ResourcingHero />}
  >
    <SplitSection
      index={1}
      align="left"
      kicker="Why us"
      title="A talent pool curated by engineers, not recruiters."
    >
      <p>
        We&rsquo;re engineers ourselves. Every candidate in our network has
        been technically screened by senior practitioners &mdash; not just
        sourced from a CV pile. You only see people we&rsquo;d be happy to
        work with on our own delivery.
      </p>
    </SplitSection>

    <SplitSection
      index={2}
      align="right"
      kicker="Engagement Models"
      title="Flexible commercial setups, no surprises."
      accent={
        <span className="sp-accent-stat">
          <span className="sp-accent-stat__num">2-3</span>
          <span className="sp-accent-stat__label">weeks to first onboard</span>
        </span>
      }
    >
      <p>
        Pick the shape that fits your business: short-term contractors to
        unblock a release, fully-managed dedicated squads, or permanent hires
        with a fair, transparent placement fee.
      </p>
      <ul>
        <li>Contract / day-rate engineers</li>
        <li>Dedicated managed QA squads</li>
        <li>Permanent placement with replacement guarantee</li>
        <li>Build-Operate-Transfer offshore teams</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={3}
      align="left"
      kicker="Roles we fill"
      title="From SDET to Head of Quality."
      accent={<span className="sp-accent-code">{'// senior > mid > junior'}</span>}
    >
      <p>
        We resource the full quality and engineering spectrum, from hands-on
        automation engineers right up to leadership roles that shape your
        engineering culture.
      </p>
      <ul>
        <li>QA Engineer / SDET / Automation Engineer</li>
        <li>Performance Test Engineer</li>
        <li>Full-stack &amp; Mobile Developers</li>
        <li>DevOps &amp; Platform Engineers</li>
        <li>QA Lead / QA Manager / Head of Quality</li>
      </ul>
    </SplitSection>

    <SplitSection
      index={4}
      align="right"
      kicker="Vetting"
      title="Tested against real work, not just trivia."
    >
      <p>
        Every engineer we recommend has been through a structured interview
        loop: pair-programming, real-world automation challenge, system
        design and cultural fit. We share the scorecards with you so you
        understand exactly what you&rsquo;re hiring.
      </p>
    </SplitSection>

    <SplitSection
      index={5}
      align="left"
      kicker="Onboarding"
      title="Productive on day five, not day fifty."
    >
      <p>
        We provide our engineers with playbooks, mentor support and access to
        our internal community. By the end of week one, they&rsquo;re raising
        PRs &mdash; not just collecting laptops.
      </p>
    </SplitSection>

    <StackRibbon
      label="Specialisms in our network"
      items={[
        'TypeScript', 'JavaScript', 'Java', 'C#', 'Python', 'Go',
        'Cypress', 'Playwright', 'Selenium', 'WebdriverIO',
        'Appium', 'XCUITest', 'Espresso',
        'k6', 'JMeter', 'Gatling',
        'AWS', 'Azure', 'GCP',
        'Docker', 'Kubernetes', 'Terraform',
      ]}
    />
  </ServicePage>
)

export default TechnicalResourcing
